import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { getMe } from '@/api/auth/me';

const AuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useApp();
  const [status, setStatus] = useState('Processing...');
  // The callback must resolve the session exactly once. A ref guard means that
  // even if this effect re-runs (e.g. a dependency identity changes), we never
  // fire a second /auth/me request — the root cause of the KX-3 request loop.
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;

    let redirectTimer: ReturnType<typeof setTimeout> | undefined;

    const handleAuth = async () => {
      try {
        console.log('========================================');
        console.log('🔵 AuthCallback Started');
        console.log('========================================');

        // Extract URL parameters from backend redirect
        const successParam = searchParams.get('success');
        const errorParam = searchParams.get('error');
        const redirectPathParam = searchParams.get('redirectPath');

        if (errorParam) {
          setStatus(`Authentication Failed: ${errorParam}`);
          return;
        }

        if (successParam === 'true') {
          setStatus('Retrieving user profile from session...');
          
          let meResponse;
          try {
            meResponse = await getMe();
          } catch (apiErr: unknown) {
            console.error('API Error (/me):', apiErr);
            setStatus(`Session Error: ${(apiErr as Error).message}`);
            return;
          }
          
          const fetchedUser = meResponse.data?.user || (meResponse as Record<string, unknown>).user;
          if (!meResponse.success || !fetchedUser) {
            setStatus('User Fetch Failed: Session not established.');
            return;
          }
          
          const user = fetchedUser;

          console.log('========================================');
          console.log('✅ Fetched User:', user);
          console.log('✅ User Role:', user.role);
          console.log('========================================');

          setStatus('Saving authentication...');

          // firstName/lastName may be returned either at the top level or nested
          // under `profile` depending on the endpoint — read both so the real
          // name is used (not the email/username fallback).
          const firstName = user.firstName ?? user.profile?.firstName;
          const lastName = user.lastName ?? user.profile?.lastName;
          const fullName = [firstName, lastName]
            .filter(Boolean)
            .join(' ')
            .trim();
          const userData = {
            id: user._id,
            name: fullName || user.name || user.username || user.email?.split('@')[0],
            email: user.email,
            role: user.role as 'admin' | 'organization' | 'operator' | 'end-user',
            phone: user.phone ?? user.profile?.phone,
            firstName,
            lastName,
            emailVerified: user.isVerified,
            isVerified: user.isVerified,
            avatar: user.avatar ?? user.profile?.avatar,
            initials: firstName && lastName
              ? `${firstName[0]}${lastName[0]}`.toUpperCase()
              : firstName
              ? firstName.substring(0, 2).toUpperCase()
              : user.email?.substring(0, 2).toUpperCase(),
          };

          localStorage.setItem('user', JSON.stringify(userData));
          setUser(userData);
          
          const redirectPaths: Record<string, string> = {
            'admin': '/kxt-mgmt',
            'organization': '/organization',
            'operator': '/operator',
            'end-user': '/user'
          };

          let finalPath = redirectPathParam || redirectPaths[user.role] || '/user';

          if (finalPath.endsWith('/dashboard')) {
            finalPath = finalPath.replace('/dashboard', '');
          }

          setStatus('Redirecting...');
          
          redirectTimer = setTimeout(() => {
            navigate(finalPath, { replace: true });
          }, 500);
          
        } else {
          setStatus('Invalid authentication callback.');
        }

      } catch (err: unknown) {
        console.error('========================================');
        console.error('❌ AuthCallback Error:', err);
        console.error('========================================');
        setStatus(`Unexpected Error: ${(err as Error).message}`);
        localStorage.removeItem('user');
      }
    };

    handleAuth();

    return () => {
      if (redirectTimer) {
        clearTimeout(redirectTimer);
      }
    };
  }, [navigate, searchParams, setUser]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f9fafb',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
        <div style={{
          width: '64px',
          height: '64px',
          border: '6px solid #e5e7eb',
          borderTopColor: '#3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 24px'
        }}></div>
        
        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '8px'
        }}>
          {status}
        </h2>
        
        <p style={{
          fontSize: '16px',
          color: status.includes('failed') || status.includes('Error') ? '#ef4444' : '#6b7280',
          wordBreak: 'break-word'
        }}>
          {status.includes('failed') || status.includes('Error') ? "Please take a screenshot of this error." : "Please wait..."}
        </p>
        
        {/* If there's a detailed error in state, we could show it here if we stored it */}
        {(status.includes('failed') || status.includes('Error')) && (
          <button 
            onClick={() => navigate('/signin')}
            style={{ marginTop: '20px', padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Back to Sign In
          </button>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;
